import React from 'react';
import { useTheme, Fade, Grid, Card, CardContent, Box, Typography, Avatar, Chip, Button, Divider } from '@mui/material';
import {
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';

const GoalsTab = ({ data = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const total = data.total || 0;
  const completed = data.completed || 0;
  const pending = data.pending || 0;
  const completionRate = data.completionRate || 0;
  const recentGoals = data.recentGoals || 0;
  const goalsList = data.list || [];

  // Funnel chart for goals progression
  const funnelData = {
    series: [
      {
        name: 'Goals',
        data: [
          { x: 'Total Goals', y: total },
          { x: 'In Progress', y: pending },
          { x: 'Completed', y: completed },
        ],
      },
    ],
    options: {
      chart: {
        type: 'bar',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%',
          borderRadius: 4,
          dataLabels: {
            position: 'center',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => val,
        style: { fontSize: '14px', fontWeight: 'bold', colors: ['#fff'] },
      },
      xaxis: {
        categories: ['Total Goals', 'In Progress', 'Completed'],
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      yaxis: {
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      colors: ['#1976d2', '#ed6c02', '#2e7d32'],
      legend: { show: false },
    },
  };

  // Gauge chart for completion rate
  const gaugeData = {
    series: [completionRate],
    options: {
      chart: {
        type: 'radialBar',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: '#e7e7e7',
            strokeWidth: '97%',
            margin: 5,
          },
          dataLabels: {
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              offsetY: -10,
            },
            value: {
              show: true,
              fontSize: '30px',
              fontWeight: 700,
              offsetY: 16,
              formatter: (val) => `${val.toFixed(1)}%`,
            },
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: ['#2e7d32'],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      colors: ['#1976d2'],
      labels: ['Completion Rate'],
    },
  };

  return (
  <Fade in timeout={500}>
    <Grid container spacing={3}>
        {/* Gauge Chart - Top Left */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                Completion Rate
              </Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Chart options={gaugeData.options} series={gaugeData.series} type="radialBar" height={300} />
            </Box>
          </Card>
        </Grid>

        {/* Funnel Chart - Top Right */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FlagIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                Goals Funnel
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Chart options={funnelData.options} series={funnelData.series} type="bar" height={300} />
            </Box>
          </Card>
        </Grid>

        {/* Stat Cards - Bottom Row with Consistent Style */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.lighter',
                }}
              >
                <FlagIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {total}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Goals
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'success.lighter',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {completed}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Completed
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'warning.lighter',
                }}
              >
                <ScheduleIcon sx={{ fontSize: 32, color: 'warning.main' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {pending}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  In Progress
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'info.lighter',
                }}
              >
                <FlagIcon sx={{ fontSize: 32, color: 'info.main' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {recentGoals}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Recent (30 days)
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Goals Details Section */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FlagIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  Recent Goals
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/dashboard/goals-details')}
                sx={{ textTransform: 'none' }}
              >
                View All Goals
              </Button>
            </Box>

            {goalsList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No goals found
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {goalsList.map((goal, index) => {
                  const user = goal.goalsFor || goal.goalsFrom;
                  const userName = user?.name || 'Unknown User';
                  const userImage = user?.image || '';
                  const userRole = user?.role || '';
                  const initials = userName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);

                  return (
                    <React.Fragment key={goal._id || index}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          sx={{
                            p: 2,
                            boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
                            borderRadius: 2,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                              transform: 'translateY(-2px)',
                            },
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar
                              src={userImage}
                              alt={userName}
                              sx={{
                                width: 48,
                                height: 48,
                                bgcolor: 'primary.main',
                              }}
                            >
                              {!userImage && initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.titles',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {userName}
                              </Typography>
                              {userRole && (
                                <Chip
                                  label={userRole}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    textTransform: 'capitalize',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mb: 1,
                                fontWeight: 500,
                              }}
                            >
                              Goal
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.titles',
                                fontWeight: 500,
                                mb: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {goal.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {goal.isCompleted ? (
                                <>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                                    Completed
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <ScheduleIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                    In Progress
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </Grid>
            )}
          </Card>
        </Grid>
    </Grid>
  </Fade>
);
};

export default GoalsTab;

